import { useCallback, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { ChargerModel, ChargerModelConnector } from "../types";
import { useToast } from "../toast";
import ConnectorDialog, { ConnectorFormData } from "./ConnectorDialog";
import ConfirmDialog from "./ConfirmDialog";

interface Props {
  open: boolean;
  model: ChargerModel | null;
  onClose: () => void;
}

export default function ChargerModelConnectorsDialog({ open, model, onClose }: Props) {
  const api = useAuthApi();
  const toast = useToast();
  const [connectors, setConnectors] = useState<ChargerModelConnector[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ChargerModelConnector | null>(null);
  const [deleting, setDeleting] = useState<ChargerModelConnector | null>(null);

  const load = useCallback(async () => {
    if (!model) return;
    setLoading(true);
    try {
      const data = await api<ChargerModelConnector[]>(`${MGMT}/charger-models/${model.id}/connectors`);
      setConnectors(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [model, toast]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleAdd = async (form: ConnectorFormData) => {
    await api(`${MGMT}/charger-models/${model!.id}/connectors`, {
      method: "POST",
      body: JSON.stringify({
        connector_number: form.connector_number,
        connector_type: form.connector_type,
        max_power_kw: form.max_power_kw !== "" ? form.max_power_kw : null,
      }),
    });
    toast.show("Connector added");
    setAdding(false);
    load();
  };

  const handleEdit = async (form: ConnectorFormData) => {
    await api(`${MGMT}/charger-models/${model!.id}/connectors/${editing!.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        connector_number: form.connector_number,
        connector_type: form.connector_type,
        max_power_kw: form.max_power_kw !== "" ? form.max_power_kw : null,
      }),
    });
    toast.show("Connector updated");
    setEditing(null);
    load();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/charger-models/${model!.id}/connectors/${deleting.id}`, { method: "DELETE" });
      toast.show("Connector removed");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Connectors — {model?.vendor} {model?.model_name}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
              Add Connector
            </Button>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : connectors.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No connectors defined yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Max Power (kW)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connectors.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.connector_number}</TableCell>
                    <TableCell>{c.connector_type}</TableCell>
                    <TableCell align="right">{c.max_power_kw ?? "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setEditing(c)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleting(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConnectorDialog
        open={adding}
        title="Add Connector"
        onSave={handleAdd}
        onClose={() => setAdding(false)}
      />

      <ConnectorDialog
        open={editing !== null}
        title="Edit Connector"
        initial={
          editing
            ? {
                connector_number: editing.connector_number,
                connector_type: editing.connector_type,
                max_power_kw: editing.max_power_kw ?? "",
              }
            : null
        }
        onSave={handleEdit}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Remove connector?"
        message={`Remove connector #${deleting?.connector_number} (${deleting?.connector_type})?`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
